import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Users, Upload, Filter, Check, X, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface BulkEnrollmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseId: string;
  courseTitle: string;
  sessions: any[];
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  job_type?: string;
  department?: string;
}

export default function BulkEnrollmentDialog({ 
  open, 
  onOpenChange, 
  courseId, 
  courseTitle,
  sessions 
}: BulkEnrollmentDialogProps) {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState('criteria');
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [csvPreview, setCsvPreview] = useState<any[]>([]);
  
  // Form state
  const [enrollmentData, setEnrollmentData] = useState({
    selectedSession: '',
    batchName: '',
    enrollmentMethod: 'criteria', // 'criteria', 'manual', 'csv'
    criteria: {
      groups: [] as string[],
      jobTypes: [] as string[],
      departments: [] as string[],
      gradeLevel: ''
    },
    csvData: ''
  });

  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchUserGroups();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, job_type, department')
        .eq('employment_status', 'active')
        .order('last_name');

      if (error) throw error;
      setAvailableUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchUserGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('user_groups')
        .select('*')
        .order('name');

      if (error) throw error;
      setUserGroups(data || []);
    } catch (error) {
      console.error('Error fetching user groups:', error);
    }
  };

  const filterUsersByCriteria = () => {
    return availableUsers.filter(user => {
      const { groups, jobTypes, departments } = enrollmentData.criteria;
      
      // If no criteria selected, return all users
      if (groups.length === 0 && jobTypes.length === 0 && departments.length === 0) {
        return true;
      }

      // Check job type
      if (jobTypes.length > 0 && !jobTypes.includes(user.job_type || '')) {
        return false;
      }

      // Check department
      if (departments.length > 0 && !departments.includes(user.department || '')) {
        return false;
      }

      return true;
    });
  };

  const handleBulkEnroll = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected for enrollment');
      return;
    }

    setLoading(true);
    try {
      const userIds = selectedUsers.map(u => u.id);
      
      const { data, error } = await supabase.rpc('bulk_enroll_users', {
        p_course_id: courseId,
        p_user_ids: userIds,
        p_enrolled_by: user?.id,
        p_session_id: enrollmentData.selectedSession || null,
        p_batch_name: enrollmentData.batchName || `${courseTitle} - Bulk Enrollment`
      });

      if (error) throw error;

      toast.success(`Successfully enrolled ${selectedUsers.length} users!`);
      onOpenChange(false);
      
      // Reset form
      setEnrollmentData({
        selectedSession: '',
        batchName: '',
        enrollmentMethod: 'criteria',
        criteria: {
          groups: [],
          jobTypes: [],
          departments: [],
          gradeLevel: ''
        },
        csvData: ''
      });
      setSelectedUsers([]);
      
    } catch (error) {
      console.error('Error bulk enrolling:', error);
      toast.error('Failed to enroll users');
    } finally {
      setLoading(false);
    }
  };

  const parseCSV = (csvText: string) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(header => header.trim().toLowerCase());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(value => value.trim());
      const row: any = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      
      data.push(row);
    }

    return data;
  };

  const handleCSVFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target?.result as string;
      try {
        const parsedData = parseCSV(csvText);
        setCsvPreview(parsedData);
        
        // Auto-match users based on email
        const emails = parsedData
          .map(row => row.email || row['email address'] || row.mail)
          .filter(email => email);
        
        const matchedUsers = availableUsers.filter(user => 
          emails.includes(user.email)
        );
        
        setSelectedUsers(matchedUsers);
        toast.success(`CSV loaded! Found ${matchedUsers.length} matching users out of ${parsedData.length} rows`);
      } catch (error) {
        toast.error('Error parsing CSV file. Please check the format.');
      }
    };
    
    reader.readAsText(file);
  };

  const handleCSVImport = () => {
    const lines = enrollmentData.csvData.trim().split('\n');
    const emails = lines.map(line => line.trim()).filter(email => email);
    
    const matchedUsers = availableUsers.filter(user => 
      emails.includes(user.email)
    );
    
    setSelectedUsers(matchedUsers);
    toast.success(`Found ${matchedUsers.length} matching users`);
  };

  const filteredUsers = enrollmentData.enrollmentMethod === 'criteria' ? 
    filterUsersByCriteria() : availableUsers;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Bulk Enrollment - {courseTitle}
          </DialogTitle>
          <DialogDescription>
            Enroll multiple users at once using different selection methods
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="criteria">Selection Criteria</TabsTrigger>
              <TabsTrigger value="manual">Manual Selection</TabsTrigger>
              <TabsTrigger value="review">Review & Enroll</TabsTrigger>
            </TabsList>

            <TabsContent value="criteria" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Enrollment Method</Label>
                  <Select 
                    value={enrollmentData.enrollmentMethod} 
                    onValueChange={(value) => setEnrollmentData({
                      ...enrollmentData, 
                      enrollmentMethod: value
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="criteria">Filter by Criteria</SelectItem>
                      <SelectItem value="manual">Manual Selection</SelectItem>
                      <SelectItem value="csv">CSV Import</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {sessions.length > 0 && (
                  <div>
                    <Label>Session (Optional)</Label>
                    <Select 
                      value={enrollmentData.selectedSession} 
                      onValueChange={(value) => setEnrollmentData({
                        ...enrollmentData, 
                        selectedSession: value
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All sessions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">All Sessions</SelectItem>
                        {sessions.map((session) => (
                          <SelectItem key={session.id} value={session.id}>
                            {new Date(session.session_date).toLocaleDateString()} - {session.start_time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div>
                <Label>Batch Name</Label>
                <Input
                  value={enrollmentData.batchName}
                  onChange={(e) => setEnrollmentData({
                    ...enrollmentData,
                    batchName: e.target.value
                  })}
                  placeholder="Enter a name for this enrollment batch"
                />
              </div>

              {enrollmentData.enrollmentMethod === 'criteria' && (
                <div className="space-y-4">
                  <div>
                    <Label>User Groups</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded p-2">
                      {userGroups.map((group) => (
                        <div key={group.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`group-${group.id}`}
                            checked={enrollmentData.criteria.groups.includes(group.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setEnrollmentData({
                                  ...enrollmentData,
                                  criteria: {
                                    ...enrollmentData.criteria,
                                    groups: [...enrollmentData.criteria.groups, group.id]
                                  }
                                });
                              } else {
                                setEnrollmentData({
                                  ...enrollmentData,
                                  criteria: {
                                    ...enrollmentData.criteria,
                                    groups: enrollmentData.criteria.groups.filter(g => g !== group.id)
                                  }
                                });
                              }
                            }}
                          />
                          <label htmlFor={`group-${group.id}`} className="text-sm">
                            {group.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Job Types</Label>
                      <Input
                        value={enrollmentData.criteria.jobTypes.join(', ')}
                        onChange={(e) => setEnrollmentData({
                          ...enrollmentData,
                          criteria: {
                            ...enrollmentData.criteria,
                            jobTypes: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                          }
                        })}
                        placeholder="Teacher, Principal, etc."
                      />
                    </div>
                    <div>
                      <Label>Departments</Label>
                      <Input
                        value={enrollmentData.criteria.departments.join(', ')}
                        onChange={(e) => setEnrollmentData({
                          ...enrollmentData,
                          criteria: {
                            ...enrollmentData.criteria,
                            departments: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                          }
                        })}
                        placeholder="Math, Science, etc."
                      />
                    </div>
                  </div>
                </div>
              )}

              {enrollmentData.enrollmentMethod === 'csv' && (
                <div className="space-y-4">
                  <div>
                    <Label>Upload CSV File</Label>
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleCSVFileUpload}
                        className="hidden"
                      />
                      <Button 
                        onClick={() => fileInputRef.current?.click()} 
                        variant="outline" 
                        className="flex-1"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Choose CSV File
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      CSV should have headers including 'email' or 'email address'
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Or Enter Email List Manually</Label>
                    <Textarea
                      value={enrollmentData.csvData}
                      onChange={(e) => setEnrollmentData({
                        ...enrollmentData,
                        csvData: e.target.value
                      })}
                      placeholder="Enter email addresses, one per line"
                      rows={4}
                    />
                    <Button onClick={handleCSVImport} variant="outline" className="w-full">
                      <Upload className="h-4 w-4 mr-2" />
                      Import from Text
                    </Button>
                  </div>

                  {csvPreview.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">CSV Preview ({csvPreview.length} rows)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="max-h-32 overflow-y-auto">
                          <div className="text-xs space-y-1">
                            {csvPreview.slice(0, 5).map((row, index) => (
                              <div key={index} className="flex gap-2 font-mono">
                                <span className="text-muted-foreground">{index + 1}:</span>
                                <span>{row.email || row['email address'] || row.mail || 'No email found'}</span>
                              </div>
                            ))}
                            {csvPreview.length > 5 && (
                              <div className="text-muted-foreground">...and {csvPreview.length - 5} more rows</div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {enrollmentData.enrollmentMethod === 'criteria' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Matching Users ({filteredUsers.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2 flex-wrap">
                      <Button 
                        size="sm" 
                        onClick={() => setSelectedUsers(filteredUsers)}
                        className="text-xs"
                      >
                        Select All ({filteredUsers.length})
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => setSelectedUsers([])}
                        className="text-xs"
                      >
                        Clear Selection
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="manual" className="space-y-4">
              <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto border rounded p-4">
                {availableUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={selectedUsers.some(u => u.id === user.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedUsers([...selectedUsers, user]);
                          } else {
                            setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
                          }
                        }}
                      />
                      <div>
                        <div className="font-medium">{user.first_name} {user.last_name}</div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {user.job_type} • {user.department}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="review" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Enrollment Summary</span>
                    <Badge variant="outline">{selectedUsers.length} users selected</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Course</Label>
                    <div className="font-medium">{courseTitle}</div>
                  </div>
                  
                  {enrollmentData.selectedSession && (
                    <div>
                      <Label>Session</Label>
                      <div className="font-medium">
                        {sessions.find(s => s.id === enrollmentData.selectedSession)?.session_date} - 
                        {sessions.find(s => s.id === enrollmentData.selectedSession)?.start_time}
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Batch Name</Label>
                    <div className="font-medium">
                      {enrollmentData.batchName || `${courseTitle} - Bulk Enrollment`}
                    </div>
                  </div>

                  <div className="max-h-48 overflow-y-auto border rounded p-4">
                    <Label>Selected Users</Label>
                    <div className="space-y-2 mt-2">
                      {selectedUsers.map((user) => (
                        <div key={user.id} className="flex items-center justify-between text-sm">
                          <span>{user.first_name} {user.last_name}</span>
                          <span className="text-muted-foreground">{user.email}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button onClick={handleBulkEnroll} disabled={loading || selectedUsers.length === 0}>
                  {loading ? 'Enrolling...' : `Enroll ${selectedUsers.length} Users`}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}